import {
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { useMemo } from "react";
import { Line } from "react-chartjs-2";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface RawData {
  [date: string]: number;
}

function fillMissingDates(data: RawData) {
  // Convert keys to Date objects and sort
  const dates = Object.keys(data)
    .map((d) => new Date(d))
    .sort((a, b) => a.getTime() - b.getTime());

  const maxMonthsAgo = new Date();
  maxMonthsAgo.setMonth(maxMonthsAgo.getMonth() - 3);

  const start = new Date(Math.max(dates[0].getTime(), maxMonthsAgo.getTime()));
  const end = new Date();

  const filled: RawData = {};
  const current = new Date(start);

  while (current <= end) {
    const isoDate = current.toISOString().split("T")[0];
    filled[isoDate] = data[isoDate] ?? 0; // use existing or default to 0
    current.setDate(current.getDate() + 1); // move to next day
  }

  return filled;
}

// Chart.js options object
const options: ChartOptions<"line"> = {
  responsive: true,
  plugins: {
    legend: {
      position: "top",
    },
    title: {
      display: true,
      text: "Email Sends Over Time",
    },
  },
  scales: {
    x: {
      ticks: {
        maxRotation: 45, // rotate at most 45°
        minRotation: 45, // rotate at least 45°
      },
      title: {
        display: true,
        text: "Date",
      },
    },
    y: {
      title: {
        display: true,
        text: "Count",
      },
      beginAtZero: true,
    },
  },
};

export default function ChartComponent({ data: rawData }: { data: RawData }) {
  const chartData = useMemo<ChartData<"line">>(() => {
    if (Object.keys(rawData).length > 0) {
      rawData = fillMissingDates(rawData);
    }

    const labels = Object.keys(rawData);
    return {
      labels,
      datasets: [
        {
          label: "Count",
          data: labels.map((d) => rawData[d]),
          borderColor: "rgba(75, 192, 192, 1)",
          backgroundColor: "rgba(75, 192, 192, 0.2)",
          tension: 0.3,
          fill: true,
        },
      ],
    };
  }, [rawData]); // <- new object whenever rawData changes

  return (
    <div className="pb-4">
      {Object.keys(rawData).length > 0 && <Line data={chartData} options={options} height={75} />}
      {Object.keys(rawData).length === 0 && (
        <h2 style={{ textAlign: "center", padding: "20px", backgroundColor: "#D6D9F0" }}>
          This mail has not been sent yet.
        </h2>
      )}
    </div>
  );
}
